/**
 * useVolumeTiers — reads volume discount tiers for the PDP PackSelector.
 *
 * The query key is shared with `useProductPage` so the BFF page bundle seeds
 * this cache on its first network round-trip. When the cache is cold (e.g.
 * the user lands on a non-PDP route that needs tiers) the hook returns an
 * empty list rather than re-fetching pricing — resolved via storefront BFF only.
 */

'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PricingTier } from '@/domain/entities/product.entity';

export interface UseVolumeTiersInput {
  productId?: string;
  variantId?: string;
  currencyCode?: string;
  countryCode?: string;
}

export function useVolumeTiers(input: UseVolumeTiersInput) {
  const { productId, variantId, currencyCode, countryCode } = input;
  const queryClient = useQueryClient();

  return useQuery<PricingTier[]>({
    queryKey: ['volume-tiers', productId, variantId, currencyCode, countryCode],
    queryFn: async () => {
      if (!productId || !currencyCode) return [];
      const cached = queryClient.getQueryData<PricingTier[]>([
        'volume-tiers',
        productId,
        variantId,
        currencyCode,
        countryCode,
      ]);
      return cached ?? [];
    },
    enabled: !!productId && !!currencyCode,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
